import { mixed, string, object, array, number, boolean } from 'yup';
import { TFunction } from 'i18next';

const saleProceedChildKeys = ['a', 'b', 'c', 'd'];
const otherIncomeChildKeys = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i'];

function isCheckedGroup(value: unknown): value is { checked?: boolean } & Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isChildRequirementMet(value: unknown, childKeys: readonly string[]) {
  if (!isCheckedGroup(value) || value.checked !== true) {
    return true;
  }

  return childKeys.some((childKey) => {
    const child = value[childKey];
    return isCheckedGroup(child) && child.checked === true;
  });
}

export function makeInvestingGoalsSchema(t: TFunction) {
  const dependentStringField = mixed().when('checked', {
    is: true,
    then: () => string().required(t('form.required')),
    otherwise: () => string().notRequired(),
  });

  return object().shape({
    a: array(string()),
    b: string(),
    d: object().shape({
      amount: number().required(t('form.required')),
      regularity: string().required(t('form.required')),
      regularly: number().required(t('form.required')),
    }),
    e: string().required(t('form.required')),
    f: object().shape({
      is: boolean(),
      // f.a is a checkbox GROUP (CheckBoxGroupField) → its value is string[], not a
      // string. Yup 1.x type-checks strictly, so string() here threw "f.a must be a
      // `string` type" once any box was ticked. Match the model's string[] shape.
      a: array(string()),
      b: string(),
      c: string(),
    }),
    g: object()
      .shape({
        a: object().shape({
          checked: boolean(),
        }),
        b: object()
          .shape({
            checked: boolean(),
            a: object().shape({
              checked: boolean(),
              gains: dependentStringField,
              notes: dependentStringField,
            }),
            b: object().shape({
              checked: boolean(),
              gains: dependentStringField,
              notes: dependentStringField,
            }),
            c: object().shape({
              checked: boolean(),
              gains: dependentStringField,
              notes: dependentStringField,
            }),
            d: object().shape({
              checked: boolean(),
              gains: dependentStringField,
              notes: dependentStringField,
            }),
          })
          .test('Required child test', t('form.required'), (value) =>
            isChildRequirementMet(value, saleProceedChildKeys),
          ),
        c: object()
          .shape({
            checked: boolean(),
            a: object().shape({
              checked: boolean(),
              notes: dependentStringField,
            }),
            b: object().shape({
              checked: boolean(),
              notes: dependentStringField,
            }),
            c: object().shape({
              checked: boolean(),
              notes: dependentStringField,
            }),
            d: object().shape({
              checked: boolean(),
              notes: dependentStringField,
            }),
            e: object().shape({
              checked: boolean(),
              notes: dependentStringField,
            }),
            f: object().shape({
              checked: boolean(),
              notes: dependentStringField,
            }),
            g: object().shape({
              checked: boolean(),
              notes: dependentStringField,
            }),
            h: object().shape({
              checked: boolean(),
              notes: dependentStringField,
            }),
            i: object().shape({
              checked: boolean(),
              notes: dependentStringField,
            }),
          })
          .test('Required child test', t('form.required'), (value) =>
            isChildRequirementMet(value, otherIncomeChildKeys),
          ),
      })
      .test(
        'At least one checked test',
        t('form.required'),
        ({ a, b, c }) => a.checked || b.checked || c.checked,
      ) as any,
  });
}
